import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import WixDashboardGate from "@/components/WixDashboardGate";
import { ThemeProvider } from "@/context/ThemeContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Customizer from "@/pages/Customizer";
import Demo from "@/pages/Demo";
import Storefront from "@/pages/Storefront";
import BlogPost from "@/pages/BlogPost";
import StoreCart from "@/pages/StoreCart";
import ProductPage from "@/pages/ProductPage";
import StoreSuccess from "@/pages/StoreSuccess";
import ShopOrders from "@/pages/ShopOrders";
import WixConnect from "@/pages/WixConnect";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import SellerPortal from "@/pages/seller/SellerPortal";
import OrderStatus from "@/pages/OrderStatus";
import Overview from "@/pages/dashboard/Overview";
import Products from "@/pages/dashboard/Products";
import ProductDetail from "@/pages/dashboard/ProductDetail";
import Logos from "@/pages/dashboard/Logos";
import Orders from "@/pages/dashboard/Orders";
import Promotions from "@/pages/dashboard/Promotions";
import Team from "@/pages/dashboard/Team";
import Analytics from "@/pages/dashboard/Analytics";
import Integrations from "@/pages/dashboard/Integrations";
import Admin from "@/pages/dashboard/Admin";
import Billing from "@/pages/dashboard/Billing";
import Settings from "@/pages/dashboard/Settings";
import StoreSettings from "@/pages/dashboard/StoreSettings";
import SiteDesigner from "@/pages/dashboard/SiteDesigner";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import DataDeletion from "@/pages/legal/DataDeletion";
import Cookies from "@/pages/legal/Cookies";
import Contact from "@/pages/legal/Contact";
import Pricing from "@/pages/legal/Pricing";
import OnboardingGuide from "@/pages/OnboardingGuide";

function RootGate() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isAppHost = !host || host.includes("emergentagent.com") || host.includes("localhost") || host.includes("127.0.0.1");
  const [state, setState] = useState(isAppHost ? { mode: "landing" } : { mode: "checking" });
  useEffect(() => {
    if (isAppHost) return;
    api.get(`/public/domain/${host}`, { withCredentials: false })
      .then((r) => setState(r.data?.slug ? { mode: "store", slug: r.data.slug } : { mode: "landing" }))
      .catch(() => setState({ mode: "landing" }));
  }, []); // eslint-disable-line
  if (state.mode === "checking") return null;
  if (state.mode === "store") return <Storefront slugOverride={state.slug} />;
  return <Landing />;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  useEffect(() => {
    const m = (window.location.hash || "").match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    (async () => {
      if (sid) { try { await api.post("/auth/google/session", { session_id: sid }); } catch { /* ignore */ } }
      window.location.replace("/dashboard");
    })();
  }, []); // eslint-disable-line
  return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0B0B0F", color: "#C6A15B", fontFamily: "sans-serif" }}>Iniciando sesión…</div>;
}

function App() {
  if (typeof window !== "undefined" && (window.location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App">
      <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootGate />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/embed/:productId" element={<Customizer />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/tienda/:slug" element={<Storefront />} />
            <Route path="/preview" element={<ProtectedRoute><Storefront preview /></ProtectedRoute>} />
            <Route path="/tienda/:slug/carrito" element={<StoreCart />} />
            <Route path="/tienda/:slug/producto/:pid" element={<ProductPage />} />
            <Route path="/tienda/:slug/p/:pageSlug" element={<Storefront />} />
            <Route path="/tienda/:slug/coleccion/:cat" element={<Storefront />} />
            <Route path="/tienda/:slug/blog/:postSlug" element={<BlogPost />} />
            <Route path="/tienda/:slug/gracias" element={<StoreSuccess />} />
            <Route path="/tienda/:slug/mis-pedidos" element={<ShopOrders />} />
            <Route path="/wix/connect" element={<WixConnect />} />
            <Route path="/vendedor" element={<SellerPortal />} />
            <Route path="/equipo" element={<SellerPortal />} />
            <Route path="/pedido/:token" element={<OrderStatus />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/guia" element={<OnboardingGuide />} />
            <Route path="/guia-de-inicio" element={<OnboardingGuide />} />
            <Route path="/dashboard" element={<WixDashboardGate><ProtectedRoute><DashboardLayout /></ProtectedRoute></WixDashboardGate>}>
              <Route index element={<Overview />} />
              <Route path="products" element={<Products />} />
              <Route path="products/:pid" element={<ProductDetail />} />
              <Route path="logos" element={<Logos />} />
              <Route path="orders" element={<Orders />} />
              <Route path="promotions" element={<Promotions />} />
              <Route path="team" element={<Team />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="admin" element={<Admin />} />
              <Route path="billing" element={<Billing />} />
              <Route path="settings" element={<Settings />} />
              <Route path="store" element={<StoreSettings />} />
              <Route path="design" element={<SiteDesigner />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
